import { Collapse } from '@material-ui/core';
import {
  SelectionEntity,
  SelectionRating,
  WarrantyDetails,
} from 'app/core/api/product-assignment/api-product-assignment.types';
import Button from 'app/shared/components/button/Button';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import Body from 'app/shared/components/typography/body/Body';
import styledFuncs from 'app/styles/styledFuncs';
import { asEnumOrUndefined } from 'app/tools/general.types';
import { sentenceCase } from 'app/tools/utils';
import _ from 'lodash';
import React, { ReactNode, useState } from 'react';
import styled from 'styled-components/macro';

const mapRatingToIcons: Record<SelectionRating, ReactNode> = {
  no: <Icon size={18} icon={IconLib.CROSS_BOXED} color="error" />,
  other: <Icon size={18} icon={IconLib.INFO} color="warning" />,
  yes: <Icon size={18} icon={IconLib.TICK_BOXED} color="primary" />,
};

const StyledTable = styled.table`
  table-layout: fixed;
  width: min(max(80%, 250px), 100%);
  margin: 0 auto ${styledFuncs.pad()};
  tr {
    display: flex;
  }
  td {
    border-bottom: none;
    &:nth-child(1) {
      padding: 4px;
    }
    &:nth-child(2) {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      padding: 0.6em 8px 1em;
      font-size: ${styledFuncs.pxToRem(10.5)};
      word-break: break-word;
      word-wrap: break-word;
      .MuiCollapse-wrapper {
        padding: 0;
        padding-top: ${styledFuncs.pad()};
      }
    }
    &:nth-child(3) {
      button {
        margin-top: -8px;
      }
    }
  }
`;

const SectionTitle = styled(Body)`
  margin: 7px;
`;

const SelectionSynthesis = styled.div`
  font-size: ${styledFuncs.pxToRem(10)};
  font-style: italic;
  color: #a8a8a8;
`;

const SelectionCollapser: React.VFC<{
  selection: SelectionEntity;
}> = ({ selection: { rating, displayValue, synthesis } }) => {
  const [open, setOpen] = useState(false);
  const validatedRating = asEnumOrUndefined(rating, SelectionRating);
  if (!validatedRating) return null;
  return (
    <>
      <tr>
        <td>{mapRatingToIcons[validatedRating]}</td>
        <td>
          {displayValue}
          {synthesis && (
            <Collapse in={open}>
              <SelectionSynthesis>{synthesis}</SelectionSynthesis>
            </Collapse>
          )}
        </td>
        <td>
          <Button
            iconButton
            onClick={() => setOpen(s => !s)}
            disabled={!synthesis}
            css={synthesis ? undefined : 'opacity: 0;'}
          >
            <Icon
              icon={open ? IconLib.CHEVRON_UP : IconLib.CHEVRON_DOWN}
              size={16}
            />
          </Button>
        </td>
      </tr>
    </>
  );
};

type Props = {
  warranty: WarrantyDetails;
};
export default function WarrantySummary({
  warranty,
}: Props): JSX.Element {
  const sections = _(warranty)
    .pick(['coverageSection', 'rightsSection'])
    .entries()
    .map(([k, v]) => ({
      ...v,
      title: sentenceCase(
        _.lowerCase(k).replace('section', '').trim()
      ),
    }))
    .value();
  return (
    <>
      {sections.map(section => (
        <div key={section.id}>
          <SectionTitle centered>{section.title}</SectionTitle>
          <StyledTable>
            <tbody>
              {section.selection?.map(selection => (
                <SelectionCollapser
                  key={selection.id}
                  {...{ selection }}
                />
              ))}
            </tbody>
          </StyledTable>
        </div>
      ))}
    </>
  );
}
